import React, { FC } from 'react';
import { Input, InputNumber, Typography } from 'antd';
import { get, merge } from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import produce from 'immer';

import { Checkbox, DateInput, IdTypeSelect, Form, NationalitySelect } from '~/components';
import InputsGroupLayout from '~/layouts/InputsGroupLayout';
import { ApplicationPayload } from '~/types/ApplicationPayload';
import { NameFn } from '~/utils/buildNamePath';
import createUid from '~/utils/createUid';
import Section from '~/layouts/Section ';
import { useValidator } from '~/controllers/validation';
import { FormItemContextProvider } from '~/components/Form/FormItemContext';
import { useGetApplication } from '~/controllers/wizard';
import { useGetDraft } from '~/controllers/draft';
import { Draft } from '~/types/Draft';
import { useOptionalFields } from '~/hooks/useOptionalFields';
import { isReadOnly } from '~/utils/isReadOnly';
import { getStagePayload } from '~/utils/getStagePayload';
import WithPep from '~/components/WithPep';
import { stepName } from '~/pages/ksa/application/shareholders';
import { Stage } from '~/types/Application';

interface Props {
  n: NameFn;
  onFinish: (values: ApplicationPayload) => any;
  initialValues?: any | undefined;
}

const BeneficialOwnerForm: FC<Props> = (props) => {
  const { onFinish, initialValues, n } = props;
  const { t } = useTranslation('ksaApplication');

  const [form] = Form.useForm<ApplicationPayload>();
  const getFormState = () => {
    return produce(initialValues || {}, (draft: any) => {
      merge(draft, form.getFieldsValue());
    });
  };
  const validatorController = useValidator(form.getFieldsValue(), undefined, Stage.ksaApplication);
  const applicationQuery = useGetApplication();
  const draftQuery = useGetDraft<Draft<any>>();

  const optionalFieldsController = useOptionalFields({ initialValues });

  const stagePayload = getStagePayload.wizard(applicationQuery.data);

  return (
    <FormItemContextProvider
      value={{
        defaultValidator: validatorController.validator,
        applicationPayload: applicationQuery.data?.payload,
        draftPayload: draftQuery.data,
        optionalFields: optionalFieldsController.optionalFields || {},
        errorFields: applicationQuery.data?.payload?.errorInfo?.errors,
        enabledFields: stagePayload?.activeStep.errorInformation?.enabledFields,
        disabledByDefault: isReadOnly(applicationQuery.data),
        disabled: isReadOnly(applicationQuery.data),
      }}
    >
      <Container className="IndividualAdd">
        <Form
          form={form}
          name={formName}
          onFinish={onFinish}
          onValuesChange={(_: any, newValues: any) => {
            optionalFieldsController.debouncedUpdate(newValues);
          }}
          initialValues={initialValues}
        >
          <Form.Item
            name={n(['uid'])}
            hidden
            initialValue={get(initialValues, n(['uid'])) || createUid()}
          />
          <Section>
            <Typography.Title level={4}>{t('Personal data').toUpperCase()}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item label={t('First name')} name={n(['personalData', 'firstName'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Last name')} name={n(['personalData', 'lastName'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Date of birth')} name={n(['personalData', 'dateOfBirth'])}>
                <DateInput disableDateRule="date <= today" />
              </Form.Item>
              <Form.Item label={t('Nationality')} name={n(['personalData', 'nationality'])}>
                <NationalitySelect />
              </Form.Item>
            </InputsGroupLayout>
            <Form.Item name={n(['personalData', 'isPep'])} valuePropName="checked">
              <Checkbox>
                <WithPep>{t('Beneficial owner is a Politically Exposed Person (PEP)')}</WithPep>
              </Checkbox>
            </Form.Item>
          </Section>

          <Section>
            <Typography.Title level={4}>{t('Passport').toUpperCase()}</Typography.Title>
            <Typography.Title level={5}>{t('Passport data:')}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item label={t('Passport number')} name={n(['passport', 'documentNumber'])}>
                <Input />
              </Form.Item>
              <Form.Item label={t('Passport issue date')} name={n(['passport', 'issueDate'])}>
                <DateInput disableDateRule="date <= today" allowClear />
              </Form.Item>
              <Form.Item label={t('Passport expiry date')} name={n(['passport', 'expiryDate'])}>
                <DateInput disableDateRule="date > today" allowClear />
              </Form.Item>
            </InputsGroupLayout>
          </Section>

          <Section>
            <Typography.Title level={4}>{t('Personal document').toUpperCase()}</Typography.Title>
            <Typography.Title level={5}>{t('Document data:')}</Typography.Title>
            <InputsGroupLayout>
              <Form.Item label={t('ID type')} name={n(['personalDocument', 'idType'])}>
                <IdTypeSelect allowClear />
              </Form.Item>
              <Form.Item label={t('ID number')} name={n(['personalDocument', 'idNumber'])}>
                <Input />
              </Form.Item>
            </InputsGroupLayout>
          </Section>

          <section>
            <Typography.Title level={4}>{t('Ownership share').toUpperCase()}</Typography.Title>
            <Typography.Paragraph>{t('Please specify ownership share')}</Typography.Paragraph>
            <InputsGroupLayout>
              <Form.Item label={t('Shares, %')} name={n(['ownershipShare', 'percent'])}>
                <InputNumber className="IndividualAdd-SharesInput" max={100} />
              </Form.Item>
            </InputsGroupLayout>
          </section>
        </Form>
      </Container>
    </FormItemContextProvider>
  );
};

export const formName = 'beneficialOwners';
export const getBeneficialOwnersFormItemsNamePath = (index: number) => [stepName, formName, index];

const Container = styled.div`
  .IndividualAdd-SharesInput {
    width: 100%;
  }
`;

export default BeneficialOwnerForm;
